import { formatSubmittedAt } from './submissionDetails';
import { termsAcceptance } from './termsAcceptance';
import React, { useEffect, useMemo, useRef, useState } from "react";

import ClarityLogo from "../assets/images/clarity-logo.png";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faChevronUp, faEnvelope, faFilePdf, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import "@fortawesome/fontawesome-svg-core";
import "@fortawesome/react-fontawesome";
import "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import { CSVLink } from "react-csv";
import config from "../config";

// AdminCasesWithDynamicFilters
const AdminCasesWithDynamicFilters = () => {
  // Auth info
  const [username] = useState(sessionStorage.getItem("username"));
  const [userFirstname] = useState(sessionStorage.getItem("firstname"));
  const [bearerToken] = useState(sessionStorage.getItem("bearerToken"));

  // UI
  const [isOpen, setIsOpen] = useState(false); // filter collapse
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showColumnOptions, setShowColumnOptions] = useState(false);

  // Filters state
  const [clientName, setClientName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [salesforceCaseId, setSalesforceCaseId] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [selectedCrmNames, setSelectedCrmNames] = useState([]);
  const [selectedSalesReps, setSelectedSalesReps] = useState([]);

  // Dynamic option lists + loading states
  const [optionsDate, setOptionsDate] = useState([]);
  const [loadingDateOptions, setLoadingDateOptions] = useState(false);

  const [optionsProducts, setOptionsProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [optionsStatus, setOptionsStatus] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [optionsOwner, setOptionsOwner] = useState([]);
  const [loadingOwner, setLoadingOwner] = useState(false);

  const [optionsCrmName, setOptionsCrmName] = useState([]);
  const [loadingCrmName, setLoadingCrmName] = useState(false);

  const [salesRepOptions, setSalesRepOptions] = useState([]);
  const [loadingSalesReps, setLoadingSalesReps] = useState(false);

  // Grid / data state
  const [currentData, setCurrentData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // sorting
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  // expanded rows/email
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedRowIdEmail, setExpandedRowIdEmail] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);

  // column visibility map
  const visibleKey = (colName) => colName.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '');
  const initialVisible = {
    Actions: true,
    ClientName: true,
    RenewalDate: true,
    RenewalType: true,
    Products: true,
    CaseStatus: true,
        SubmittedAt: true,
        SubmittedBy: true,
        TermsAccepted: true,
        TermsAcceptedBy: true,
    SalesforceCaseID: true,
    CRMSalesforceID: true,
    CRMName: true,
    CaseOwnerName: true,
    RenewalFirstName: true,
    RenewalLastName: true,
    RenewalContactEmail: true,
    SalesRepName: true,
    EstimatedRevenue: true,
    CurrentStatusDate: true,
  };
  const [visibleColumns, setVisibleColumns] = useState(initialVisible);

  // selections for exports/print
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [selectedCaseType, setSelectedCaseType] = useState(null);

  // set axios auth header if token present
  useEffect(() => {
    if (bearerToken) axios.defaults.headers.common["Authorization"] = `Bearer ${bearerToken}`;
  }, [bearerToken]);

  // ===========
  // Dynamic options fetch (runs once on mount)
  // ===========
  useEffect(() => {
    // helper to map server value to {value,label}
    const toOption = (v) => {
      if (v == null) return null;
      if (typeof v === "string" || typeof v === "number") return { value: String(v), label: String(v) };
      // if object with id/name or value/label
      if (v.value && v.label) return v;
      if (v.id && v.name) return { value: String(v.id), label: String(v.name) };
      // fallback to JSON
      return { value: JSON.stringify(v), label: String(v) };
    };

    const load = async () => {
      // products
      setLoadingProducts(true);
      setLoadingStatus(true);
      setLoadingOwner(true);
      setLoadingCrmName(true);
      setLoadingSalesReps(true);
      setLoadingDateOptions(true);

      try {
        const [
          productsResp,
          statusesResp,
          ownersResp,
          crmResp,
          salesRepsResp,
          datesResp
        ] = await Promise.all([
          axios.get(`${config.API_URL}/RenewalCases/GetFilterProducts`),
          axios.get(`${config.API_URL}/RenewalCases/GetFilterStatuses`),
          axios.get(`${config.API_URL}/RenewalCases/GetFilterOwners`),
          axios.get(`${config.API_URL}/RenewalCases/GetFilterCrmNames`),
          axios.get(`${config.API_URL}/RenewalCases/GetFilterSalesReps`),
          axios.get(`${config.API_URL}/RenewalCases/GetFilterRenewalDates`)
        ]);

        // map responses safely (support both array-of-strings and array-of-objects)
        const mapList = (resp) => {
          const arr = resp?.data ?? [];
          if (!Array.isArray(arr)) return [];
          return arr.map(toOption).filter(Boolean);
        };

        setOptionsProducts(mapList(productsResp));
        setOptionsStatus(mapList(statusesResp));
        setOptionsOwner(mapList(ownersResp));
        setOptionsCrmName(mapList(crmResp));
        setSalesRepOptions(mapList(salesRepsResp));
        // For dates, ensure ISO strings or display-friendly labels
        setOptionsDate(mapList(datesResp));
      } catch (err) {
        console.error("Failed to load filter options", err);
        // fall back to empty arrays (UI remains functional)
        setOptionsProducts([]);
        setOptionsStatus([]);
        setOptionsOwner([]);
        setOptionsCrmName([]);
        setSalesRepOptions([]);
        setOptionsDate([]);
      } finally {
        setLoadingProducts(false);
        setLoadingStatus(false);
        setLoadingOwner(false);
        setLoadingCrmName(false);
        setLoadingSalesReps(false);
        setLoadingDateOptions(false);
      }
    };

    load();
    
  }, []);

  // ===========
  // Data fetching for table (server-side)
  // ===========
  const toCsv = (selectedArray) => (selectedArray && selectedArray.length ? selectedArray.map((s) => s.value).join(",") : "");

  const fetchPage = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || "", // unused search box separate
        clientNameFilter: clientName || "",
        sfCaseIdFilter: salesforceCaseId || "",
        productsFilter: toCsv(selectedProducts),
        caseStatusFilter: toCsv(selectedStatuses),
        caseOwnerFilter: toCsv(selectedOwners),
        crmFilter: toCsv(selectedCrmNames),
        salesRepFilter: toCsv(selectedSalesReps),
        renewalDtFilter: toCsv(selectedDates),
      };

      if (sortKey) {
        params.sortBy = sortKey;
        params.sortOrder = sortOrder;
      }

      const [rowsResp, countResp] = await Promise.all([
        axios.get(`${config.API_URL}/RenewalCases/GetAdminData`, { params }),
        axios.get(`${config.API_URL}/RenewalCases/GetAdminDataCount`, { params }),
      ]);

      const rows = Array.isArray(rowsResp.data) ? rowsResp.data : rowsResp.data.items ?? [];
      const total = typeof countResp.data === "number" ? countResp.data : countResp.data.totalCount ?? 0;

      setCurrentData(rows);
      setTotalCount(total);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching page:", err);
      setCurrentData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // initial load of first page
  useEffect(() => {
    fetchPage(1);
    
  }, [rowsPerPage]);

  useEffect(() => {
    // Debounce typing for 500ms then fetch page 1
    const t = setTimeout(() => {
      // only trigger if searchTerm changed (it is in deps)
      fetchPage(1);
    }, 500);

    return () => clearTimeout(t);
    
  }, [searchTerm]);

  // pagination helpers
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage || 1));
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage + 1;
  const indexOfLastRow = Math.min(currentPage * rowsPerPage, totalCount);

  // handlers (filter & grid)
  const handleFilter = () => fetchPage(1);
  const handleClear = () => {
    setSearchTerm("");
    setClientName("");
    setSelectedDates([]);
    setSalesforceCaseId("");
    setSelectedProducts([]);
    setSelectedStatuses([]);
    setSelectedOwners([]);
    setSelectedCrmNames([]);
    setSelectedSalesReps([]);
    setTimeout(() => fetchPage(1), 30);
  };
  const handlePerPageChange = (e) => { const v = Number(e.target.value); setRowsPerPage(v); fetchPage(1); };
  const handlePageChange = (page) => { if (page < 1 || page > totalPages) return; fetchPage(page); };
  const toggleColumnVisibility = (name) => setVisibleColumns((p) => ({ ...p, [name]: !p[name] }));
  const handleSort = (key) => { if (sortKey === key) setSortOrder((o) => (o === "asc" ? "desc" : "asc")); else { setSortKey(key); setSortOrder("asc"); } fetchPage(1); };
  const handleRowExpand = (caseId) => setExpandedRowId((cur) => (cur === caseId ? null : caseId));
  const isRowExpanded = (caseId) => expandedRowId === caseId;

  // email expand & fetch logs
  const handleRowExpandEmail = async (caseId) => {
    if (expandedRowIdEmail === caseId) { setExpandedRowIdEmail(null); setEmailLogs([]); return; }
    setExpandedRowIdEmail(caseId);
    setEmailLoading(true);
    try {
      const resp = await axios.get(`${config.API_URL}/EmailLogs/${caseId}`, { params: { caseId } });
      setEmailLogs(Array.isArray(resp.data) ? resp.data : resp.data.items ?? []);
    } catch (err) {
      console.error("Error fetching email logs", err);
      setEmailLogs([]);
    } finally {
      setEmailLoading(false);
    }
  };
  const isRowExpandedEmail = (caseId) => expandedRowIdEmail === caseId;

  // export helpers
  const copyFiltered = async () => {
    try {
      const text = JSON.stringify(currentData, null, 2);
      if (navigator.clipboard) { await navigator.clipboard.writeText(text); alert("Data copied to clipboard!"); }
      else { const el = document.createElement("textarea"); el.value = text; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el); alert("Copied!"); }
    } catch (err) { console.error(err); alert("Copy failed"); }
  };
  const csvData = useMemo(() => currentData.map((r) => ({
    caseId: r.caseId, employerName: r.employerName, renewalDate: r.renewalDate, caseType: r.caseType,
    submittedAt: r.submittedAt || "", submittedBy: r.submittedBy || "",
    termsAccepted: termsAcceptance(r).accepted, termsAcceptedBy: termsAcceptance(r).acceptedBy,
    products: r.products, caseStatus: r.caseStatus, sfCaseId: r.sfCaseId, crmSalesforceId: r.crmSalesforceId,
    crmName: r.crmName, caseOwnerName: r.caseOwnerName, renewalContactFirstName: r.renewalContactFirstName,
    renewalContactLastName: r.renewalContactLastName, renewalContactEmail: r.renewalContactEmail,
    salesRepName: r.salesRepName, clarityRevenue: r.clarityRevenue, updatedAt: r.updatedAt
  })), [currentData]);

  const handleCasePrint = (row) => { setSelectedCaseId(row.caseId); setSelectedCaseType(row.caseType); console.log("Print", row.caseId); };
  const handleWccExport = async (row) => {
    try {
      const resp = await axios.get(`${config.API_URL}/RenewalPortal/WccDataExport`, { params: { caseId: row.caseId }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `wcc_${row.caseId}.csv`); document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { console.error(err); alert("WCC export failed"); }
  };

  const getSortIcon = (key) => { if (!key) return null; if (sortKey !== key) return "⇅"; return sortOrder === "asc" ? "▲" : "▼"; };

  // columns kept from original
  const columns = useMemo(() => [
    {
      name: 'Actions',
      selector: (row) => (
        <div key={row.caseId} className='d-flex align-items-center '>
          <div className={`comunication-button ${isRowExpandedEmail(row.caseId) ? 'active' : ''}`} style={{ cursor: 'pointer' }} onClick={() => handleRowExpandEmail(row.caseId)}>
            <FontAwesomeIcon icon={faEnvelope} />
          </div>

          <div className="summary-button mx-2" style={{ cursor: 'pointer' }} onClick={() => handleCasePrint(row)}>
            <FontAwesomeIcon icon={faFilePdf} />
          </div>

          <a href={row.caseType === 'COBRA' ? `${config.WEB_URL}/Cobra/${row.caseId}` : `${config.WEB_URL}/EmployeeBenefits/${row.caseId}`} target="_parent" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <div className="external-link" style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </div>
          </a>

          <div className={`circle ms-2 ${isRowExpanded(row.caseId) ? 'active' : ''}`} onClick={() => handleRowExpand(row.caseId)} style={{ cursor: 'pointer' }}>
            +
          </div>

          {row.caseType === "COBRA" && (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${row.caseId}`}>Export WCC Data</Tooltip>}>
              <div className="wcc-export-button mx-2" style={{ cursor: "pointer" }} onClick={() => handleWccExport(row)}>
                WCC
              </div>
            </OverlayTrigger>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: '136.625px',
      sticky: 'left',
      left: '0px',
      key: 'actions'
    },
    { name: 'Client Name', selector: row => row.employerName, key: 'employerName', sortable: true, sticky: 'left', style: { position: 'sticky', left: '136.25px', width: '200.625px' } },
    { name: 'Renewal Date', selector: row => row.renewalDate, key: 'renewalDate', sortable: true, sticky: 'left', style: { position: 'sticky', left: '337.531px', zIndex: 3, backgroundColor: '#fff' }, width: '200px' },
    { name: 'Renewal Type', key: 'caseType', selector: row => row.caseType, sortable: true },
    { name: 'Products', key: 'products', selector: row => row.products, sortable: true },
    { name: 'Case Status', key: 'caseStatus', selector: row => row.caseStatus, sortable: true },
    { name: 'Submitted At', key: 'submittedAt', selector: row => formatSubmittedAt(row.submittedAt), sortable: false },
    { name: 'Submitted By', key: 'submittedBy', selector: row => row.submittedBy || '—', sortable: false },
        // { name: 'Terms Accepted', key: 'termsAccepted', selector: row => termsAcceptance(row).accepted, sortable: false },
        // { name: 'Terms Accepted By', key: 'termsAcceptedBy', selector: row => termsAcceptance(row).acceptedBy, sortable: false },
    { name: 'Salesforce Case ID', key: 'sfCaseId', selector: row => row.sfCaseId, sortable: true },
    { name: 'CRM Salesforce ID', key: 'crmSalesforceId', selector: row => row.crmSalesforceId, sortable: true },
    { name: 'CRM Name', key: 'crmName', selector: row => row.crmName, sortable: true },
    { name: 'Case Owner Name', key: 'caseOwnerName', selector: row => row.caseOwnerName, sortable: true },
    { name: 'Renewal First Name', key: 'renewalContactFirstName', selector: row => row.renewalContactFirstName, sortable: true },
    { name: 'Renewal Last Name', key: 'renewalContactLastName', selector: row => row.renewalContactLastName, sortable: true },
    { name: 'Renewal Contact Email', key: 'renewalContactEmail', selector: row => row.renewalContactEmail, sortable: true },
    { name: 'Sales Rep Name', key: 'salesRepName', selector: row => row.salesRepName, sortable: true },
    { name: 'Estimated Revenue', key: 'clarityRevenue', selector: row => row.clarityRevenue, sortable: true },
    {
      name: 'Current Status Date',
      selector: row => {
        if (!row.updatedAt) return "";
        const date = new Date(row.updatedAt);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      },
      sortable: true,
      key: 'updatedAt'
    }
  ], []); // stable

  const getLeftFor = (name) => (name === "Actions" ? "0px" : name === "Client Name" ? "136.25px" : name === "Renewal Date" ? "339px" : "unset");

  return (
    <div className="admin-panel">
      <div id="layout-wrapper">
        <header id="page-topbar">
          <nav className="navbar navbar-expand-lg navbar-light navbar-custom">
            <div className="container-fluid">
              <a className="navbar-brand" href="#"><img src={ClarityLogo} alt="Bootstrap" height="45" /></a>
              <div className="collapse navbar-collapse" id="navbarSupportedContent">
                {username && (
                  <ul className="navbar-nav ms-auto">
                    <li className="nav-item"><span className="nav-link">Welcome {userFirstname}</span></li>
                    <li className="nav-item"><a className="nav-link" href="#">Logout</a></li>
                  </ul>
                )}
              </div>
            </div>
          </nav>
        </header>

        <div className="main-content home-main-content">
          <div className="page-content">
            <div className="container-fluid home-container">
              <div className="container" style={{ maxWidth: "1440px", margin: "auto", marginTop: "3rem" }}>
                <h3 className="mb-4">New Renewal - Admin Panel</h3>

                {/* Filters card */}
                <div className="card mb-3">
                  <div className="card-header" id="filterHeading">
                    <h5 className="mb-0">
                      <button className="btn btn-link"
                          id="filterRecords"
                          data-toggle="collapse"
                          data-target="#filterCollapse" onClick={() => setIsOpen((v) => !v)} aria-expanded={isOpen}>
                      <FontAwesomeIcon icon={faFilter} />
                          Filters{" "}
                          <FontAwesomeIcon
                            icon={faChevronUp}
                            className={`ms-3 ${isOpen ? "rotate" : ""}`}
                          />
                      </button>
                    </h5>
                  </div>

                  {isOpen && (
                    <div id="filterCollapse" className="collapse show" aria-labelledby="filterHeading">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Search by clientName.</Tooltip>}>
                              <input type="text" className="form-control mb-3" placeholder="Search By Client Name" value={clientName} onChange={(e) => setClientName(e.target.value)} />
                            </OverlayTrigger>
                          </div>

                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Renewal Date</Tooltip>}>
                              <Select id="impDtFilter" className="selectpicker" options={optionsDate} isMulti placeholder="Renewal Date" value={selectedDates} onChange={(s) => setSelectedDates(s || [])} isLoading={loadingDateOptions} />
                            </OverlayTrigger>
                          </div>

                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Search By SF Case Id</Tooltip>}>
                              <input type="text" className="form-control mb-3" placeholder="Search By SF Case Id" value={salesforceCaseId} onChange={(e) => setSalesforceCaseId(e.target.value)} />
                            </OverlayTrigger>
                          </div>

                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Search By Products</Tooltip>}>
                              <Select id="searchby-products" options={optionsProducts} isMulti placeholder="Search By Products" value={selectedProducts} onChange={(s) => setSelectedProducts(s || [])} isLoading={loadingProducts} />
                            </OverlayTrigger>
                          </div>

                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Search By Case status</Tooltip>}>
                              <Select id="caseStatusFilter" options={optionsStatus} isMulti placeholder="Case Status" value={selectedStatuses} onChange={(s) => setSelectedStatuses(s || [])} isLoading={loadingStatus} />
                            </OverlayTrigger>
                          </div>

                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Search By Case Owner</Tooltip>}>
                              <Select id="caseOwnerFilter" options={optionsOwner} isMulti placeholder="Case Owners" value={selectedOwners} onChange={(s) => setSelectedOwners(s || [])} isLoading={loadingOwner} />
                            </OverlayTrigger>
                          </div>

                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Search By Crm Name</Tooltip>}>
                              <Select id="crmFilter" options={optionsCrmName} isMulti placeholder="CRM Name" value={selectedCrmNames} onChange={(s) => setSelectedCrmNames(s || [])} isLoading={loadingCrmName} />
                            </OverlayTrigger>
                          </div>

                          <div className="col-md-2">
                            <OverlayTrigger placement="top" overlay={(props) => <Tooltip {...props}>Search By Sales Rep Name</Tooltip>}>
                              <Select id="salesRepFilter" options={salesRepOptions} isMulti placeholder="Sales Rep Name" value={selectedSalesReps} onChange={(s) => setSelectedSalesReps(s || [])} isLoading={loadingSalesReps} />
                            </OverlayTrigger>
                          </div>
                        </div>

                        <div className="row mt-2">
                          <div className="col-md-12 text-end">
                            <button className="btn ad-button me-2" onClick={handleFilter}>Search</button>
                            <button className="btn clear-button" onClick={handleClear}>Clear</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Table card */}
                <div className="card mb-4">
                  <div className="card-body">
                    <div id="dataTable_wrapper" className="dt-container dt-empty-footer">
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                        <div className="dt-length">
                          <select name="dataTable_length" className="dt-input " value={rowsPerPage} onChange={handlePerPageChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <label htmlFor="dt-length-0"> entries per page</label>
                        </div>

                        <div className="dt-search">
                          <label htmlFor="dt-search-0">Search:</label>
                          <input type="search" className="dt-input " id="dt-search-0" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="" />
                        </div>

                        <div className="dt-buttons">
                          <button className="dt-button buttons-collection" onClick={() => setShowExportOptions((s) => !s)} aria-haspopup="dialog" aria-expanded={showExportOptions ? 'true' : 'false'}>
                            <span>Export</span> <span className="dt-button-down-arrow">▼</span>
                          </button>

                          {showExportOptions && (
                            <div className="dt-button-collection dtb-b2" style={{ position: 'absolute', width: '200px', zIndex: 10, backgroundColor: 'white', border: '1px solid #ccc', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', padding: '10px', borderRadius: '5px' }}>
                              <div role="menu">
                                <button className="dt-button buttons-copy buttons-html5" onClick={copyFiltered}>Copy</button>
                                <br />
                                <CSVLink data={csvData} filename={"admin_data.csv"} className="dt-button buttons-csv buttons-html5" target="_blank"><span>CSV</span></CSVLink>
                              </div>
                            </div>
                          )}

                          <button className="dt-button buttons-collection buttons-colvis" onClick={() => setShowColumnOptions((s) => !s)}>
                            <span>Change columns</span> <span className="dt-button-down-arrow">▼</span>
                          </button>

                          {showColumnOptions && (
                            <div className="dt-button-collection" style={{ zIndex: 999, border: '1px solid #ccc', position: 'absolute', backgroundColor: 'white', width: '200px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', padding: '10px', borderRadius: '5px' }}>
                              <div role="menu">
                                {columns.map((col) => {
                                  const visKey = visibleKey(col.name);
                                  return (
                                    <div key={col.name} className="form-check">
                                      <input className="form-check-input" type="checkbox" checked={visibleColumns[visKey] ?? true} onChange={() => toggleColumnVisibility(visKey)} id={`checkbox-${visKey}`} />
                                      <label className="form-check-label" htmlFor={`checkbox-${visKey}`}>{col.name}</label>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="dt-scroll">
                        <div className="dt-scroll" style={{ overflowX: 'auto', position: 'relative' }}>
                          <table className="table table-striped dataTable table-bordered" style={{ minWidth: '1000px' }}>
                            <thead className="sticky-header">
                              <tr>
                                {columns.map((col, index) => {
                                  const visKey = visibleKey(col.name);
                                  if (!visibleColumns[visKey]) return null;
                                  return (
                                    <th key={index} onClick={() => col.sortable && handleSort(col.key)} style={{ position: col.sticky ? 'sticky' : 'relative', cursor: col.sortable ? 'pointer' : 'default', left: getLeftFor(col.name), zIndex: col.sticky ? 3 : undefined, whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                                      {col.name} {col.sortable && <span style={{ marginLeft: 6 }}>{getSortIcon(col.key)}</span>}
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>

                            {summaryLoading || loading ? (
                              <tbody>
                                {Array(5).fill().map((_, rowIndex) => (
                                  <tr key={rowIndex}>
                                    {columns.map((col, colIndex) => {
                                      const visKey = visibleKey(col.name);
                                      if (!visibleColumns[visKey]) return null;
                                      return <td key={colIndex}><div className="skeleton" style={{ height: 20, width: col.name === 'Actions' ? 50 : 150, background: '#eee' }} /></td>;
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            ) : (
                              <tbody>
                                {currentData.map((row) => (
                                  <React.Fragment key={row.caseId}>
                                    <tr style={{ backgroundColor: '#fff' }}>
                                      {columns.map((col, colIndex) => {
                                        const visKey = visibleKey(col.name);
                                        if (!visibleColumns[visKey]) return null;
                                        return (
                                          <td key={colIndex} style={{ position: col.sticky ? 'sticky' : 'relative', left: getLeftFor(col.name), zIndex: col.sticky ? 3 : undefined }}>
                                            {typeof col.selector === "function" ? col.selector(row) : col.selector}
                                          </td>
                                        );
                                      })}
                                    </tr>

                                    {isRowExpanded(row.caseId) && (
                                      <tr>
                                        <td colSpan={columns.length}>
                                          <div className="additional-info col-md-12 mb-3">
                                            <h5>Details</h5>
                                            <div className="responsive-table">
                                              <table className="table">
                                                <tbody>
                                                  <tr><th>Client Name</th><td>{row.employerName}</td></tr>
                                                  <tr><th>Renewal Date</th><td>{row.renewalDate}</td></tr>
                                                  <tr><th>Estimated Revenue</th><td>{row.clarityRevenue}</td></tr>
                                                  <tr><th>CRM Salesforce ID</th><td>{row.crmSalesforceId}</td></tr>
                                                  <tr><th>Sales Rep Name</th><td>{row.salesRepName}</td></tr>
                                                  <tr><th>Products</th><td>{row.products}</td></tr>
                                                  <tr><th>Renewal Type</th><td>{row.caseType}</td></tr>
                                                  <tr><th>Submitted At</th><td>{formatSubmittedAt(row.submittedAt)}</td></tr>
                                                  <tr><th>Submitted By</th><td>{row.submittedBy || "—"}</td></tr>
                                                  <tr><th>Current Status Date</th><td>{new Date(row.updatedAt).toISOString().split('T')[0]}</td></tr>
                                                  <tr><th>Case Status</th><td>{row.caseStatus}</td></tr>
                                                  <tr><th>Case Owner Name</th><td>{row.caseOwnerName}</td></tr>
                                                  <tr><th>Salesforce Case ID</th><td>{row.sfCaseId}</td></tr>
                                                  <tr><th>CRM Name</th><td>{row.crmName}</td></tr>
                                                  <tr><th>Renewal Contact Email</th><td>{row.renewalContactEmail}</td></tr>
                                                  <tr><th>Renewal First Name</th><td>{row.renewalContactFirstName}</td></tr>
                                                  <tr><th>Renewal Last Name</th><td>{row.renewalContactLastName}</td></tr>
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    )}

                                    {isRowExpandedEmail(row.caseId) && (
                                      <tr>
                                        <td colSpan={columns.length}>
                                          <div className="additional-info col-md-12 mb-3">
                                            <h5>Communications</h5>
                                            {emailLoading ? <p>Loading...</p> : (
                                              <div className="responsive-table mb-3">
                                                <table className="table">
                                                  <thead><tr><th>Subject</th><th>Sent To</th><th>TemplateId</th><th>Sent Date</th></tr></thead>
                                                  <tbody>
                                                    {emailLogs.length > 0 ? emailLogs.map((log, idx) => (
                                                      <tr key={idx}><td>{log.subject}</td><td>{log.emailTo}</td><td>{log.templateId}</td><td>{new Date(log.createdAt).toLocaleString()}</td></tr>
                                                    )) : <tr><td colSpan="4">No email logs available.</td></tr>}
                                                  </tbody>
                                                </table>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            )}
                          </table>
                        </div>
                      </div>

                      <div className="pagination-controls d-flex justify-content-between mt-3">
                        <div>Showing {indexOfFirstRow} to {indexOfLastRow} of {totalCount} entries</div>
                        <div className="pagination-buttons">
                          <button className={`${currentPage === 1 ? 'disabled' : ''}`} onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
                          <button className={`${currentPage === 1 ? 'disabled' : ''}`} onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                          <span className="mx-3">Page {currentPage} of {totalPages}</span>
                          <button className={`${currentPage === totalPages ? 'disabled' : ''}`} onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                          <button className={`${currentPage === totalPages ? 'disabled' : ''}`} onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hidden print targets (wire your print components here) */}
                {selectedCaseId && selectedCaseType && (
                  <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', display: 'none' }}>
                    {/* Example: <CobraSummary caseId={selectedCaseId} ref={printRef} /> */}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCasesWithDynamicFilters;
